import { useCallback, useEffect, useState } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiService } from 'api/services/ApiService';
import { Logger } from 'utilities';
import { DEFAULT_DROPBOX_ROOT, DROPBOX_FOLDER_STATUS } from './dropbox.constants';
import type { DropboxFolderOption, DropboxFolderStatus } from './dropbox.types';
import { buildDropboxPath, normalizeFolderSegment, resolveDropboxPath } from './dropbox.utils';
import useDropbox from './useDropbox';
import { DROPBOX_FOLDER_SYNC_MESSAGES, DROPBOX_FOLDER_SYNC_QUERY_KEYS } from './useDropboxFolderSync.constants';

interface DropboxFolderSyncParams {
  open: boolean;
  valuationId: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentCompany: Record<string, any> | undefined | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  companyMeasurementDateById: Record<string, any> | undefined | null;
  isLoadingMeasurementDate: boolean;
  measurementDateError: unknown;
  suggestedCompanyFolderName: string;
  suggestedValuationFolderName: string;
  queryClient: QueryClient;
}

const useDropboxFolderSync = ({
  open,
  valuationId,
  currentCompany,
  companyMeasurementDateById,
  isLoadingMeasurementDate,
  measurementDateError,
  suggestedCompanyFolderName,
  suggestedValuationFolderName,
  queryClient,
}: DropboxFolderSyncParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const { checkFolderExists, searchFolders, createFolder, listFolder, checkIsAuthenticated, connectDropbox } = useDropbox();

  const [dropboxConnected, setDropboxConnected] = useState<boolean | null>(null);
  const [dropboxRoot, setDropboxRoot] = useState<string>(DEFAULT_DROPBOX_ROOT);
  const [companyFolderStatus, setCompanyFolderStatus] = useState<DropboxFolderStatus>(DROPBOX_FOLDER_STATUS.IDLE);
  const [valuationFolderStatus, setValuationFolderStatus] = useState<DropboxFolderStatus>(DROPBOX_FOLDER_STATUS.IDLE);
  const [companyFolderSuggestions, setCompanyFolderSuggestions] = useState<DropboxFolderOption[]>([]);
  const [valuationFolderSuggestions, setValuationFolderSuggestions] = useState<DropboxFolderOption[]>([]);
  const [companyFolderMatchPaths, setCompanyFolderMatchPaths] = useState<string[]>([]);
  const [valuationFolderMatchPaths, setValuationFolderMatchPaths] = useState<string[]>([]);
  const [companyFolderSimilarMatchesCount, setCompanyFolderSimilarMatchesCount] = useState(0);
  const [valuationFolderSimilarMatchesCount, setValuationFolderSimilarMatchesCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
    setValuationFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
    setCompanyFolderSuggestions([]);
    setValuationFolderSuggestions([]);
    setCompanyFolderMatchPaths([]);
    setValuationFolderMatchPaths([]);
    setCompanyFolderSimilarMatchesCount(0);
    setValuationFolderSimilarMatchesCount(0);
  }, [open, valuationId]);

  useEffect(() => {
    if (!open) return;
    checkIsAuthenticated()
      .then(setDropboxConnected)
      .catch(() => setDropboxConnected(false));
  }, [open, checkIsAuthenticated]);

  const dedupeDropboxOptions = useCallback((options: DropboxFolderOption[]) => {
    const byPath = new Map<string, DropboxFolderOption>();
    options.forEach(option => {
      if (!option.pathDisplay?.trim()) return;
      if (!byPath.has(option.pathDisplay)) byPath.set(option.pathDisplay, option);
    });
    return Array.from(byPath.values());
  }, []);

  const handleSearchCompanyFolder = useCallback(async (folderName: string) => {
    const normalizedFolderName = normalizeFolderSegment(folderName);
    const query = normalizedFolderName || suggestedCompanyFolderName;
    const scopePath = buildDropboxPath(dropboxRoot);

    setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.SEARCHING);
    try {
      const folderOptions = await searchFolders(query, scopePath);
      setCompanyFolderSuggestions(current => dedupeDropboxOptions([...current, ...folderOptions]));
      if (!query) {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
        setCompanyFolderMatchPaths([]);
        setCompanyFolderSimilarMatchesCount(0);
        return;
      }
      const matchedPaths = Array.from(
        new Set(
          folderOptions
            .filter(option => normalizeFolderSegment(option.name).toLowerCase() === query.toLowerCase())
            .map(option => option.pathDisplay)
            .filter(Boolean)
        )
      );
      const hasMatch = matchedPaths.length > 0;
      setCompanyFolderMatchPaths(matchedPaths);
      if (hasMatch) {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setCompanyFolderSimilarMatchesCount(0);
      } else if (folderOptions.length > 0) {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.MATCHES);
        setCompanyFolderSimilarMatchesCount(folderOptions.length);
      } else {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
        setCompanyFolderSimilarMatchesCount(0);
      }
    } catch (err) {
      Logger.error('Error searching company folders in Dropbox', err);
      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
      setCompanyFolderMatchPaths([]);
      setCompanyFolderSimilarMatchesCount(0);
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SEARCH_COMPANY_FOLDER_ERROR, { variant: 'error' });
    }
  }, [dedupeDropboxOptions, dropboxRoot, enqueueSnackbar, searchFolders, suggestedCompanyFolderName]);

  const handleSearchValuationFolder = useCallback(async (folderName: string) => {
    const normalizedCompanyFolder = resolveDropboxPath(currentCompany?.file_storage_reference || '', dropboxRoot);
    if (!normalizedCompanyFolder) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SET_COMPANY_FOLDER_FIRST, { variant: 'info' });
      return;
    }
    const normalizedFolderName = normalizeFolderSegment(folderName);
    const query = normalizedFolderName || suggestedValuationFolderName;

    setValuationFolderStatus(DROPBOX_FOLDER_STATUS.SEARCHING);
    try {
      const folderOptions = await listFolder(normalizedCompanyFolder);
      setValuationFolderSuggestions(current => dedupeDropboxOptions([...current, ...folderOptions]));
      if (!query) {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.IDLE);
        setValuationFolderMatchPaths([]);
        setValuationFolderSimilarMatchesCount(0);
        return;
      }
      const matchedPaths = Array.from(
        new Set(
          folderOptions
            .filter(option => normalizeFolderSegment(option.name).toLowerCase() === query.toLowerCase())
            .map(option => option.pathDisplay)
            .filter(Boolean)
        )
      );
      const hasMatch = matchedPaths.length > 0;
      setValuationFolderMatchPaths(matchedPaths);
      if (hasMatch) {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setValuationFolderSimilarMatchesCount(0);
      } else if (folderOptions.length > 0) {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.MATCHES);
        setValuationFolderSimilarMatchesCount(folderOptions.length);
      } else {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
        setValuationFolderSimilarMatchesCount(0);
      }
    } catch (err) {
      Logger.error('Error listing valuation folders in Dropbox', err);
      setValuationFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
      setValuationFolderMatchPaths([]);
      setValuationFolderSimilarMatchesCount(0);
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.LIST_VALUATION_FOLDER_ERROR, { variant: 'error' });
    }
  }, [currentCompany?.file_storage_reference, dedupeDropboxOptions, dropboxRoot, enqueueSnackbar, listFolder, suggestedValuationFolderName]);

  const handleCreateCompanyFolder = useCallback(async (folderName: string) => {
    const normalizedFolderName = normalizeFolderSegment(folderName);
    if (!normalizedFolderName) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.ENTER_COMPANY_FOLDER_NAME, { variant: 'info' });
      return;
    }
    const companyPath = buildDropboxPath(dropboxRoot, normalizedFolderName);
    setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.CREATING);
    try {
      const createResult = await createFolder(companyPath);
      if (createResult === DROPBOX_FOLDER_STATUS.EXISTS) {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setCompanyFolderMatchPaths([companyPath]);
        setCompanyFolderSimilarMatchesCount(0);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_EXISTS, { variant: 'info' });
      } else {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.CREATED);
        setCompanyFolderMatchPaths([companyPath]);
        setCompanyFolderSimilarMatchesCount(0);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_CREATED, { variant: 'success' });
      }
      setCompanyFolderSuggestions(current =>
        dedupeDropboxOptions([...current, { name: normalizedFolderName, pathDisplay: companyPath }])
      );
    } catch (err) {
      Logger.error('Error creating company folder in Dropbox', err);
      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_CREATE_ERROR, { variant: 'error' });
    }
  }, [createFolder, dedupeDropboxOptions, dropboxRoot, enqueueSnackbar]);

  const handleCreateValuationFolder = useCallback(async (folderName: string) => {
    const normalizedCompanyFolder = resolveDropboxPath(currentCompany?.file_storage_reference || '', dropboxRoot);
    if (!normalizedCompanyFolder) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SET_COMPANY_FOLDER_BEFORE_VALUATION, { variant: 'info' });
      return;
    }
    const normalizedFolderName = normalizeFolderSegment(folderName);
    if (!normalizedFolderName) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.ENTER_VALUATION_FOLDER_NAME, { variant: 'info' });
      return;
    }
    const valuationPath = buildDropboxPath(dropboxRoot, normalizedCompanyFolder, normalizedFolderName);
    setValuationFolderStatus(DROPBOX_FOLDER_STATUS.CREATING);
    try {
      const createResult = await createFolder(valuationPath);
      if (createResult === DROPBOX_FOLDER_STATUS.EXISTS) {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
        setValuationFolderMatchPaths([valuationPath]);
        setValuationFolderSimilarMatchesCount(0);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_EXISTS, { variant: 'info' });
      } else {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.CREATED);
        setValuationFolderMatchPaths([valuationPath]);
        setValuationFolderSimilarMatchesCount(0);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_CREATED, { variant: 'success' });
      }
      setValuationFolderSuggestions(current =>
        dedupeDropboxOptions([...current, { name: normalizedFolderName, pathDisplay: valuationPath }])
      );
    } catch (err) {
      Logger.error('Error creating valuation folder in Dropbox', err);
      setValuationFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_CREATE_ERROR, { variant: 'error' });
    }
  }, [createFolder, currentCompany?.file_storage_reference, dedupeDropboxOptions, dropboxRoot, enqueueSnackbar]);

  const handleUpdateCompanyFolder = useCallback(async (folderName: string, selectedFolderPath?: string | null) => {
    const normalizedFolderName = normalizeFolderSegment(folderName);
    if (!normalizedFolderName) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.ENTER_COMPANY_FOLDER_NAME_SYNC, { variant: 'info' });
      return;
    }
    if (!currentCompany?.id) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_ID_NOT_AVAILABLE, { variant: 'error', autoHideDuration: 8000 });
      Logger.error('Company ID not available', { currentCompany });
      return;
    }
    const companyPath = resolveDropboxPath(selectedFolderPath || normalizedFolderName, dropboxRoot);
    setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.SYNCING);
    try {
      const folderExists = await checkFolderExists(companyPath);
      if (!folderExists) {
        setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
        setCompanyFolderMatchPaths([]);
        setCompanyFolderSimilarMatchesCount(0);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.FOLDER_NOT_IN_DROPBOX, { variant: 'warning' });
        return;
      }
      await ApiService.apiCompaniesPartialUpdate(currentCompany.id, {
        file_storage_reference: companyPath,
        is_deleted: false,
      } as Parameters<typeof ApiService.apiCompaniesPartialUpdate>[1]);
      await queryClient.invalidateQueries({
        queryKey: [DROPBOX_FOLDER_SYNC_QUERY_KEYS.COMPANY_MEASUREMENT_DATE_BY_ATTIO_VALUATION_ID, valuationId],
      });
      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
      setCompanyFolderMatchPaths([companyPath]);
      setCompanyFolderSimilarMatchesCount(0);
      setCompanyFolderSuggestions(current =>
        dedupeDropboxOptions([...current, { name: normalizedFolderName, pathDisplay: companyPath }])
      );
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_UPDATED, { variant: 'success' });
    } catch (err) {
      Logger.error('Error updating company folder:', err);
      setCompanyFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
      setCompanyFolderMatchPaths([]);
      setCompanyFolderSimilarMatchesCount(0);
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_FOLDER_UPDATE_ERROR, { variant: 'error' });
    }
  }, [checkFolderExists, currentCompany, dedupeDropboxOptions, dropboxRoot, enqueueSnackbar, queryClient, valuationId]);

  const handleUpdateValuationFolder = useCallback(async (folderName: string, selectedFolderPath?: string | null) => {
    const normalizedFolderName = normalizeFolderSegment(folderName);
    if (!normalizedFolderName) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.ENTER_VALUATION_FOLDER_NAME_SYNC, { variant: 'info' });
      return;
    }
    const normalizedCompanyFolder = resolveDropboxPath(currentCompany?.file_storage_reference || '', dropboxRoot);
    if (!normalizedCompanyFolder) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.SET_COMPANY_FOLDER_BEFORE_VALUATION_SYNC, { variant: 'info' });
      return;
    }
    if (isLoadingMeasurementDate) {
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.LOADING_MEASUREMENT_DATE, { variant: 'info' });
      return;
    }
    const measurementDateId = companyMeasurementDateById?.id;
    if (!measurementDateId) {
      const errorMessage = measurementDateError
        ? DROPBOX_FOLDER_SYNC_MESSAGES.MEASUREMENT_DATE_NOT_LINKED
        : DROPBOX_FOLDER_SYNC_MESSAGES.MEASUREMENT_DATE_NOT_AVAILABLE;
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      Logger.error('Measurement date ID not available', { companyMeasurementDateById, valuationId, measurementDateError });
      return;
    }
    const valuationPath = resolveDropboxPath(
      selectedFolderPath || buildDropboxPath(normalizedCompanyFolder, normalizedFolderName),
      dropboxRoot
    );
    setValuationFolderStatus(DROPBOX_FOLDER_STATUS.SYNCING);
    try {
      const folderExists = await checkFolderExists(valuationPath);
      if (!folderExists) {
        setValuationFolderStatus(DROPBOX_FOLDER_STATUS.NOT_FOUND);
        setValuationFolderMatchPaths([]);
        setValuationFolderSimilarMatchesCount(0);
        enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.FOLDER_NOT_IN_DROPBOX, { variant: 'warning' });
        return;
      }
      await ApiService.apiCompanyMeasurementDatesPartialUpdate(measurementDateId, {
        file_storage_reference: normalizedFolderName,
      });
      await queryClient.invalidateQueries({
        queryKey: [DROPBOX_FOLDER_SYNC_QUERY_KEYS.COMPANY_MEASUREMENT_DATE_BY_ATTIO_VALUATION_ID, valuationId],
      });
      setValuationFolderStatus(DROPBOX_FOLDER_STATUS.EXISTS);
      setValuationFolderMatchPaths([valuationPath]);
      setValuationFolderSimilarMatchesCount(0);
      setValuationFolderSuggestions(current =>
        dedupeDropboxOptions([...current, { name: normalizedFolderName, pathDisplay: valuationPath }])
      );
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_UPDATED, { variant: 'success' });
    } catch (err) {
      Logger.error('Error updating valuation folder:', err);
      setValuationFolderStatus(DROPBOX_FOLDER_STATUS.ERROR);
      setValuationFolderMatchPaths([]);
      setValuationFolderSimilarMatchesCount(0);
      enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.VALUATION_FOLDER_UPDATE_ERROR, { variant: 'error' });
    }
  }, [
    checkFolderExists,
    companyMeasurementDateById,
    currentCompany?.file_storage_reference,
    dedupeDropboxOptions,
    dropboxRoot,
    enqueueSnackbar,
    isLoadingMeasurementDate,
    measurementDateError,
    queryClient,
    valuationId,
  ]);

  return {
    dropboxConnected,
    connectDropbox,
    dropboxRoot,
    setDropboxRoot,
    companyFolderStatus,
    valuationFolderStatus,
    companyFolderSuggestions,
    valuationFolderSuggestions,
    companyFolderMatchPaths,
    valuationFolderMatchPaths,
    companyFolderSimilarMatchesCount,
    valuationFolderSimilarMatchesCount,
    handleSearchCompanyFolder,
    handleSearchValuationFolder,
    handleCreateCompanyFolder,
    handleCreateValuationFolder,
    handleUpdateCompanyFolder,
    handleUpdateValuationFolder,
  };
};

export default useDropboxFolderSync;
